type TUserAgentData = {
 brands?: { brand: string; version: string }[]
 mobile?: boolean
}

// Chromium-based browsers and in-app webviews that also put "Chrome/" in their UA
const NON_CHROME_UA =
 /Edg|OPR|Opera|SamsungBrowser|YaBrowser|Vivaldi|Brave|UCBrowser|CriOS|FxiOS|EdgiOS|; wv\)|FBAN|FBAV|Instagram|Line\//i

const MOBILE_UA = /Android|iPhone|iPad|iPod|Mobile|Tablet/i

type TNavigator = Navigator & { userAgentData?: TUserAgentData }

// True on phones and tablets, whatever the browser
export const isMobileDevice = (): boolean => {
 if (typeof navigator === 'undefined') return false
 const { userAgentData } = navigator as TNavigator
 if (typeof userAgentData?.mobile === 'boolean') return userAgentData.mobile
 return MOBILE_UA.test(navigator.userAgent)
}

// Other WebKit browsers on macOS that also put "Safari/" in their UA
const NON_SAFARI_UA =
 /Chrome|Chromium|CriOS|FxiOS|EdgiOS|Firefox|OPR|DuckDuckGo/i

// True only for Apple Safari on a Mac (not iPad, which also reports "Macintosh")
export const isDesktopSafari = (): boolean => {
 if (typeof navigator === 'undefined') return false
 const ua = navigator.userAgent
 const isSafari =
  navigator.vendor === 'Apple Computer, Inc.' &&
  /Version\/[\d.]+.*Safari\//.test(ua) &&
  !NON_SAFARI_UA.test(ua)
 // iPadOS requests desktop sites with a Mac UA; touch points give it away
 const isMac = /Macintosh/.test(ua) && navigator.maxTouchPoints <= 1
 return isSafari && isMac
}

// The web app is supported on Google Chrome and Safari for desktop (AR0547)
export const isSupportedBrowser = (): boolean => {
 if (typeof navigator === 'undefined') return true

 // Desktop Safari has no client hints, so check it before the Chrome logic
 if (isDesktopSafari()) return true

 const uaData = (navigator as TNavigator).userAgentData

 // Client hints give the real brand: Edge, Brave and Opera report their own name
 if (uaData?.brands?.length) {
  const isChrome = uaData.brands.some((b) => b.brand === 'Google Chrome')
  return isChrome && !uaData.mobile
 }

 // Fallback for browsers without client hints (Safari, Firefox, older Chrome)
 const ua = navigator.userAgent
 const isChrome =
  /Chrome\//.test(ua) &&
  navigator.vendor === 'Google Inc.' &&
  !NON_CHROME_UA.test(ua)
 return isChrome && !MOBILE_UA.test(ua)
}
